import { API_ENDPOINT, Logger, QueryKeys } from "@cede/utils";
import { useApi } from "@/hooks/core/useApi";
import { useQuery } from "@tanstack/react-query";

/**
 * Hook to fetch bid/ask market rate for a trading pair.
 * Uses public endpoint, no authentication required.
 */
export const usePublicMarketRate = ({ exchangeId, pairSymbol }: { exchangeId?: string; pairSymbol?: string }) => {
  const apiHelper = useApi();

  const { data: marketRate, isLoading } = useQuery({
    queryKey: [QueryKeys.PUBLIC_MARKET_RATE, exchangeId, pairSymbol],
    queryFn: async () => {
      if (!exchangeId || !pairSymbol) return null;

      try {
        const result = await apiHelper.getOrThrow(API_ENDPOINT.HUB_TRADE_PUBLIC_MARKET_RATE, {
          queryParams: { exchangeId, pairSymbol },
        });
        return {
          bid: parseFloat(result.bid),
          ask: parseFloat(result.ask),
        };
      } catch (error) {
        Logger.error("Error fetching public market rate:", error);
        return null;
      }
    },
    enabled: !!exchangeId && !!pairSymbol,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  return { marketRate: marketRate ?? null, isLoading };
};
